import { useEffect, useState } from "react";
import { Gauge, Hand } from "lucide-react";

import { api } from "../../api/client";
import { cn } from "@/lib/utils";

/**
 * Bandeau d'identité de cockpit (dual-cockpit 2026-07-31, paire « aviation ») :
 * « Autopilote » (cyan, moteur CH10) / « Commandes » (bleu, cockpit manuel).
 * Affiche le COMPTE ENGAGÉ en clair — sur du LIVE, savoir quel compte l'écran
 * engage vaut plus que tout le reste. Pose aussi le titre d'onglet navigateur.
 * Le compte : passé en prop (Commandes — déjà dans le status manuel) ou lu une
 * fois sur /api/mode/status (Autopilote).
 */
export default function CockpitIdentity({
  variant,
  account,
}: {
  variant: "auto" | "manual";
  /** Compte engagé ; undefined = à résoudre via /api/mode/status (variant auto). */
  account?: string | null;
}) {
  const isAuto = variant === "auto";
  const [fetched, setFetched] = useState<string | null>(null);

  useEffect(() => {
    document.title = `${isAuto ? "Autopilote" : "Commandes"} — DAX Bot`;
    return () => {
      document.title = "DAX Bot";
    };
  }, [isAuto]);

  useEffect(() => {
    if (isAuto && account === undefined) {
      api
        .modeStatus()
        .then((m) => setFetched(m.accountId))
        .catch(() => {
          // Silencieux — la bannière system-state porte l'injoignabilité backend.
        });
    }
  }, [isAuto, account]);

  const engaged = account !== undefined ? account : fetched;
  const Icon = isAuto ? Gauge : Hand;

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-xl border px-4 py-3",
        isAuto
          ? "border-accent-neon/40 bg-accent-neon/5 shadow-[inset_0_-2px_0_var(--color-accent-neon)]"
          : "border-info/40 bg-info-soft shadow-[inset_0_-2px_0_var(--color-info)]",
      )}
      data-testid={`cockpit-identity-${variant}`}
    >
      <div
        className={cn(
          "grid size-9 shrink-0 place-items-center rounded-lg border",
          isAuto
            ? "border-accent-neon/40 bg-accent-neon/10 text-accent-neon"
            : "border-info/40 bg-info/10 text-info",
        )}
      >
        <Icon className="size-5" aria-hidden />
      </div>
      <div className="min-w-0">
        <div className="text-foreground text-base font-bold leading-tight">
          {isAuto ? "Autopilote" : "Commandes"}
        </div>
        <div className="text-muted-foreground text-xs">
          {isAuto
            ? "cockpit automatique · champion · l'humain n'y touche pas"
            : "cockpit manuel · tes décisions · rails d'exécution §19"}
        </div>
      </div>
      <div className="ml-auto text-right">
        <div className="text-muted-foreground text-[10px] uppercase tracking-widest">
          Compte engagé
        </div>
        <div
          className={cn("font-mono text-sm font-semibold", isAuto ? "text-accent-neon" : "text-info")}
          data-testid={`cockpit-account-${variant}`}
        >
          {engaged ?? "—"}
        </div>
      </div>
    </div>
  );
}
